import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Drawer.module.css';

// Side panel — a record preview, a filter panel, a detail view that must not cost
// the user the list behind it. Closes on Esc with focus returning to the trigger
// (06-qol §2).

interface DrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly meta?: string;
  readonly footer?: ReactNode;
  /** Opt-in controls beside Close, such as RNK-02's Previous / "N of M" / Next (Redesign Group 3). */
  readonly headerActions?: ReactNode;
  /** `wide` (R5, design card "Ranking"): a detail with a document beside it, such as RNK-02 and its CV. */
  readonly size?: 'default' | 'wide';
  readonly children: ReactNode;
}

export function Drawer({
  isOpen,
  onClose,
  title,
  meta,
  footer,
  headerActions,
  size = 'default',
  children,
}: DrawerProps): ReactNode {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  // The latest onClose, read at Esc: a caller's new function each render must not re-run the effect, which
  // would pull focus back to the panel from a control inside it (Previous / Next) on every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    openerRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onCloseRef.current();
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      openerRef.current?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.scrim}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={size === 'wide' ? `${styles.panel} ${styles.wide}` : styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <div className={styles.headerText}>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            {meta !== undefined && <span className={styles.meta}>{meta}</span>}
          </div>
          {headerActions !== undefined && (
            <div className={styles.headerActions}>
              {headerActions}
              <span className={styles.headerDivider} aria-hidden />
            </div>
          )}
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <X size={18} aria-hidden />
          </button>
        </header>
        <div className={styles.body}>{children}</div>
        {footer !== undefined && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
